import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { formatDistanceToNow } from 'date-fns';
import { useDashboardStats, useRecentActivity, useProgressTrends, useLabPerformance } from '../hooks';
import AtRiskWidget from '../components/AtRiskWidget';
import styles from './Dashboard.module.css';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const Dashboard: React.FC = () => {
  const [chartPeriod, setChartPeriod] = useState<7 | 14 | 30>(14);
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useDashboardStats();
  const { data: activity, isLoading: activityLoading } = useRecentActivity(15);
  const { data: trends } = useProgressTrends(chartPeriod);
  const { data: labPerformance } = useLabPerformance();

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetchStats();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'submission':
        return '📝';
      case 'completion':
        return '✅';
      case 'start':
        return '🚀';
      case 'environment':
        return '🖥️';
      case 'question':
        return '❓';
      default:
        return '📌';
    }
  };

  const getScoreClass = (score: number) => {
    if (score >= 85) return styles.high;
    if (score >= 70) return styles.medium;
    return styles.low;
  };

  // Chart configuration
  const chartData = {
    labels: trends?.map((t) => {
      const date = new Date(t.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }) || [],
    datasets: [
      {
        label: 'Completions',
        data: trends?.map((t) => t.completions) || [],
        borderColor: '#22c55e',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Submissions',
        data: trends?.map((t) => t.submissions) || [],
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  if (statsLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner} />
        <span>Loading dashboard...</span>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <div>
          <h1>Dashboard</h1>
          <p>Welcome back! Here's what's happening with your students.</p>
        </div>
        <div className={styles.headerActions}>
          <button
            className={`${styles.refreshBtn} ${isRefreshing ? styles.spinning : ''}`}
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <span>🔄</span>
            Refresh
          </button>
        </div>
      </header>

      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <div className={`${styles.statIcon} ${styles.blue}`}>👥</div>
            <span className={`${styles.statTrend} ${styles.up}`}>+{stats?.newStudentsThisWeek || 0}</span>
          </div>
          <div className={styles.statValue}>{stats?.totalStudents || 0}</div>
          <div className={styles.statLabel}>Total Students</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <div className={`${styles.statIcon} ${styles.green}`}>🟢</div>
          </div>
          <div className={styles.statValue}>{stats?.activeStudents || 0}</div>
          <div className={styles.statLabel}>Active Now</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <div className={`${styles.statIcon} ${styles.orange}`}>📝</div>
          </div>
          <div className={styles.statValue}>{stats?.submissionsPending || 0}</div>
          <div className={styles.statLabel}>Pending Reviews</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <div className={`${styles.statIcon} ${styles.purple}`}>🖥️</div>
          </div>
          <div className={styles.statValue}>{stats?.activeEnvironments || 0}</div>
          <div className={styles.statLabel}>Active Environments</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <div className={styles.statIcon}>📊</div>
          </div>
          <div className={styles.statValue}>{stats?.averageScore || 0}%</div>
          <div className={styles.statLabel}>Avg. Score</div>
        </div>
      </div>

      {/* Students needing attention — full-width row between stats and main content */}
      <div className={styles.atRiskRow}>
        <AtRiskWidget />
      </div>

      {/* Main Content */}
      <div className={styles.mainGrid}>
        {/* Charts Section */}
        <div className={styles.chartsSection}>
          <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <h3>Progress Overview</h3>
              <div className={styles.chartTabs}>
                <button
                  className={`${styles.chartTab} ${chartPeriod === 7 ? styles.active : ''}`}
                  onClick={() => setChartPeriod(7)}
                >
                  7 days
                </button>
                <button
                  className={`${styles.chartTab} ${chartPeriod === 14 ? styles.active : ''}`}
                  onClick={() => setChartPeriod(14)}
                >
                  14 days
                </button>
                <button
                  className={`${styles.chartTab} ${chartPeriod === 30 ? styles.active : ''}`}
                  onClick={() => setChartPeriod(30)}
                >
                  30 days
                </button>
              </div>
            </div>
            <div className={styles.chartContainer}>
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>

        {/* Activity Feed */}
        <div className={styles.activitySection}>
          <div className={styles.activityHeader}>
            <h3>Recent Activity</h3>
            <span className={styles.activityBadge}>Live</span>
          </div>
          <div className={styles.activityList}>
            {activityLoading ? (
              <div className={styles.empty}>Loading activity...</div>
            ) : activity && activity.length > 0 ? (
              activity.map((item) => (
                <div key={item.id} className={styles.activityItem}>
                  <div className={`${styles.activityIcon} ${styles[item.type]}`}>
                    {getActivityIcon(item.type)}
                  </div>
                  <div className={styles.activityContent}>
                    <p>
                      <strong>{item.studentName}</strong> {item.message}
                    </p>
                    <span className={styles.activityTime}>
                      {formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.empty}>No recent activity</div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Grid */}
      <div className={styles.bottomGrid}>
        {/* Lab Performance */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>Lab Performance</h3>
            <Link to="/labs" className={styles.viewAllLink}>
              View all
            </Link>
          </div>
          {labPerformance && labPerformance.length > 0 ? (
            <table className={styles.labTable}>
              <thead>
                <tr>
                  <th>Lab</th>
                  <th>Pass Rate</th>
                  <th>Avg Score</th>
                </tr>
              </thead>
              <tbody>
                {labPerformance.slice(0, 5).map((lab) => (
                  <tr key={lab.labId}>
                    <td className={styles.labTitle}>{lab.labTitle}</td>
                    <td>
                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressFill}
                          style={{ width: `${lab.passRate}%` }}
                        />
                      </div>
                    </td>
                    <td className={`${styles.scoreCell} ${getScoreClass(lab.averageScore)}`}>
                      {lab.averageScore}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.empty}>No lab data available</div>
          )}
        </div>

        {/* Quick Actions */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>Quick Actions</h3>
          </div>
          <div className={styles.actionGrid}>
            <Link to="/grading" className={styles.actionBtn}>
              <span>📝</span>
              Grade Submissions
            </Link>
            <Link to="/announcements" className={styles.actionBtn}>
              <span>📢</span>
              New Announcement
            </Link>
            <Link to="/students" className={styles.actionBtn}>
              <span>👥</span>
              View Students
            </Link>
            <Link to="/environments" className={styles.actionBtn}>
              <span>🖥️</span>
              Manage Environments
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
